import React from 'react'
import { useIntl } from 'react-intl'
import styled from '@emotion/styled'
import { Box } from '@mui/material'
import { ui } from '../../../../utils/uiTokens'
import { LEVEL_COLORS, percentOf, studentLevel } from '../ratingLevel'

interface Props {
    firstName?: string
    lastName?: string
    subtitle: string
    totalPoint: number
    maxPoint: number
    rank: number
}

// Шапка отчёта в цвете уровня ученика: одна на экране «Детальдар» и в PDF/PNG
const ReportStudentCard: React.FC<Props> = ({ firstName, lastName, subtitle, totalPoint, maxPoint, rank }) => {
    const { formatMessage } = useIntl()
    const level = studentLevel(totalPoint, maxPoint)
    const c = level ? LEVEL_COLORS[level] : null
    const pct = percentOf(totalPoint, maxPoint)

    return (
        <Card
            style={{
                background: c?.bg || ui.surface,
                borderColor: c?.border || ui.border,
                borderLeft: c ? `5px solid ${c.accent}` : undefined,
            }}
        >
            <Avatar style={{ color: c?.text || ui.pink }}>
                {(firstName?.[0] || '') + (lastName?.[0] || '')}
            </Avatar>

            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Name>
                    {firstName} {lastName}
                </Name>
                <Muted>{subtitle}</Muted>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <Muted style={{ fontSize: 13 }}>{formatMessage({ id: 'weekTotal' })}</Muted>
                <Total style={{ color: c?.text || ui.pink }}>
                    {totalPoint}
                    <small> / {maxPoint}</small>
                </Total>
                {c && (
                    <Badge style={{ background: ui.card, color: c.text, border: `1px solid ${c.accent}` }}>
                        {pct}%
                    </Badge>
                )}
                {rank > 0 && (
                    <Badge
                        style={{
                            background: c?.bg || ui.pinkSoft,
                            color: c?.text || ui.pink,
                            border: `1px solid ${c?.border || ui.pinkSoft}`,
                        }}
                    >
                        {formatMessage({ id: 'placeInGroup' }, { rank })}
                    </Badge>
                )}
            </Box>
        </Card>
    )
}

export default ReportStudentCard

const Card = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
    margin-top: 20px;
    padding: 18px 20px;
    border: 1px solid;
    border-radius: 14px;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;

    * {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
    }
`

const Avatar = styled.div`
    width: 56px;
    height: 56px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    font-weight: 600;
    background: ${ui.card};
    flex-shrink: 0;
`

const Name = styled.div`
    font-size: 20px;
    font-weight: 700;
    color: ${ui.ink};
`

const Muted = styled.div`
    font-size: 12px;
    color: ${ui.muted};
`

const Total = styled.div`
    font-size: 30px;
    font-weight: 700;
    line-height: 1;

    small {
        font-size: 14px;
        font-weight: 600;
        color: ${ui.muted};
    }
`

const Badge = styled.span`
    padding: 4px 12px;
    border-radius: 100px;
    font-size: 13px;
    font-weight: 700;
    white-space: nowrap;
`
